<template>
    <div class="date-container">
        <template v-if="isInputFormat()">
            <div class="date-label" v-if="label">
                {{ label }}
            </div>
            <flex-container class="input-container" style="grid-gap: 10px; margin-top: 5px">
                <DefaultDropdown
                    with-input
                    simple-input
                    scroll-active
                    class="full-width"
                    :cases="daysCases"
                    :readonly="readonly"
                    :active-case="days"
                    trimmedLabel
                    @update="days = $event"
                />
                <DefaultDropdown
                    with-input
                    simple-input
                    scroll-active
                    :readonly="readonly"
                    class="full-width"
                    :cases="monthsCases"
                    :active-case="months"
                    trimmedLabel
                    @update="months = $event"
                />
                <DefaultDropdown
                    with-input
                    simple-input
                    scroll-active
                    class="full-width"
                    :cases="yearsCases"
                    :active-case="years"
                    :readonly="readonly"
                    trimmedLabel
                    @update="years = $event"
                />
            </flex-container>
        </template>
        <DefaultDropdown
            v-else
            class="counties-dropdown"
            :label="(!multiple && label) || ''"
            :style-modifier="styleModifier"
            :active-case="value"
            :border-color="borderColor"
            maxFlatWidth="345px"
            flexible-label
            :readonly="readonly"
            :required="required"
            ref="dropdown"
            trimmedLabel
            :cases="[]"
        >
            <template v-if="tooltip" #tooltip>
                <alert-with-question>
                    <default-title
                        :weight="500"
                        font-family="Inter, sans-serif"
                        class="mb-5"
                        v-html="label"
                    ></default-title>
                    <default-description font-family="Inter, sans-serif" v-html="tooltip"></default-description>
                </alert-with-question>
            </template>
            <template #modal>
                <CustomCalendar
                    :allow-year-select="true"
                    :allow-double-click="false"
                    :select-date="activeCase"
                    @get="handleDate"
                />
            </template>
        </DefaultDropdown>
    </div>
</template>

<script>
import FlexContainer from "../Containers/FlexContainer.vue"
import AlertWithQuestion from "../Alerts/AlertWithQuestion.vue"
import CustomCalendar from "../CustomCalendar/CustomCalendar.vue"
import DefaultDropdown from "../Forms/DefaultDropdown.vue"
import DefaultDescription from "../Typography/DefaultDescription.vue"
import DefaultTitle from "../Typography/DefaultTitle.vue"
import LocalTranslator from "../../mixins/local-translator"

import { DateTime } from "luxon"

const currentYear = new Date().getFullYear()
const formatO = val => (val < 10 ? `0${val}` : val)

export default {
    components: {
        FlexContainer,
        CustomCalendar,
        DefaultDescription,
        DefaultDropdown,
        AlertWithQuestion,
        DefaultTitle
    },
    props: {
        label: {
            type: String,
            default: ""
        },
        format: {
            type: [Object, Array],
            default: null
        },
        validation: {
            type: [Object, Array],
            default: null
        },
        borderColor: {
            type: String,
            default: "#E0E0EC"
        },
        required: {
            type: Boolean,
            default: false
        },
        tooltip: {
            type: String,
            default: ""
        },
        activeCase: {
            type: [Object, Array, String, Number, Date],
            default: null
        },
        cases: {
            type: [Object, Array],
            default: null
        },
        withAbsent: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        styleModifier: {
            type: [String, Array],
            default: ""
        },
        readonly: {
            type: Boolean,
            default: false
        },
        trimmedLabel: {
            type: Boolean,
            default: true
        }
    },
    model: {
        prop: "activeCase",
        event: "update"
    },
    name: "DefaultDateDropdown",
    mixins: [LocalTranslator],
    data() {
        return {
            localKeys: "course",
            monthsCases: new Array(12).fill({ id: 0, value: 0 }).map((_, index) => formatO(index + 1)),
            daysCases: new Array(31).fill({ id: 0, value: 0 }).map((_, index) => formatO(index + 1)),
            yearsCases: new Array(151).fill(0).map((_, index) => currentYear + 50 - index)
        }
    },
    methods: {
        isInputFormat() {
            return this.format && this.format.date && this.format.date === "[DAY] [MONTH] [YEAR]"
        },
        handleDate($event) {
            this.$emit("update", $event.date)
            this.$refs.dropdown.hide()
        }
    },
    computed: {
        days: {
            get() {
                if (this.activeCase) {
                    const date = DateTime.fromISO(new Date(this.activeCase).toISOString()).setLocale(
                        window.app_current_locale
                    )
                    return formatO(date.day)
                }
                return ""
            },
            set(val) {
                let date = this.activeCase
                    ? DateTime.fromISO(new Date(this.activeCase).toISOString())
                    : DateTime.local()
                if (val <= 31) {
                    date = date.set({ day: Number(val) })
                }
                this.$emit("update", `${date.toFormat("yyyy-MM-dd")}T00:00:00`)
            }
        },
        months: {
            get() {
                if (this.activeCase) {
                    const date = DateTime.fromISO(new Date(this.activeCase).toISOString()).setLocale(
                        window.app_current_locale
                    )
                    return formatO(date.month)
                }
                return ""
            },
            set(val) {
                let date = this.activeCase
                    ? DateTime.fromISO(new Date(this.activeCase).toISOString())
                    : DateTime.local()
                if (val <= 12) {
                    date = date.set({ month: Number(val) })
                }
                this.$emit("update", `${date.toFormat("yyyy-MM-dd")}T00:00:00`)
            }
        },
        years: {
            get() {
                if (this.activeCase) {
                    const date = DateTime.fromISO(new Date(this.activeCase).toISOString())
                    return date.year
                }
                return ""
            },
            set(val) {
                let date = this.activeCase
                    ? DateTime.fromISO(new Date(this.activeCase).toISOString())
                    : DateTime.local()
                date = date.set({ year: Number(val) })
                this.$emit("update", `${date.toFormat("yyyy-MM-dd")}T00:00:00`)
            }
        },
        value() {
            if (this.activeCase) {
                let date

                if (typeof this.activeCase === "string") {
                    if (this.activeCase.includes("T")) {
                        date = DateTime.fromISO(this.activeCase)
                    } else {
                        date = DateTime.fromJSDate(new Date(this.activeCase))
                    }
                } else if (this.activeCase instanceof Date) {
                    date = DateTime.fromJSDate(this.activeCase)
                }

                // Возвращаем отформатированную строку

                const formatString = (this?.format?.date || "dd MMMM yyyy")
                    .replace("[DAY]", "dd")
                    .replace("[MONTH]", "MMMM")
                    .replace("[YEAR]", "yyyy")
                    .replace("DD", "dd")
                    .replace("YYYY", "yyyy")

                return date.setLocale(window.app_current_locale).toFormat(formatString)
            }
            return this.activeCase
        }
    }
}
</script>


<style lang="sass" scoped>
.date-container
    container-type: inline-size
@container (max-width: 370px)
    .input-container
        flex-direction: column !important
.date-label
    font-size: 12px
    margin-left: 30px
    margin-bottom: 5px
.full-width
    width: 100%
.input-container
    @media (max-width: 410px)
        flex-direction: column !important
</style>
